// @flow
import * as React from 'react';

import { Outlet, Route } from 'react-router-dom';
import { compose } from 'redux';

import { TrackingProvider } from '@gandi/tracking';
import { provideTranslate } from '@gandi/translate';

import { provideAssetPath } from 'gandi.react-asset-path';

import CartConfigure from './Cart';
import Account from './Cart/Account';
import AccountCreate from './Cart/Account/Create';
import AccountEdit from './Cart/Account/Edit';
import AccountChoice from './Cart/AccountChoice';
import AccountChoiceConfirmCustomer from './Cart/AccountChoiceConfirmCustomer';
import AccountChoiceConfirmOrganization from './Cart/AccountChoiceConfirmOrganization';
import AuthInfo from './Cart/AuthInfo';
import Claims from './Cart/Claims';
import Customer from './Cart/Customer';
import CustomerCreate from './Cart/Customer/Create';
import CustomerEdit from './Cart/Customer/Edit';
import DomainContactEdition from './Cart/DomainContacts/DomainContactEdition';
import DomainContactsWizard from './Cart/DomainContacts/Wizard';
import SetupOverview from './Cart/Setup/Overview';
import SetupRoleContact from './Cart/Setup/RoleContact';
import Smd from './Cart/Smd';
import Templates from './Cart/Templates';

import CheckoutIndex from './Checkout';
import AwaitingPayment from './Checkout/AwaitingPayment';
import Checkout from './Checkout/Checkout';
import GenerateProforma from './Checkout/GenerateProforma';
import PaymentReturnWrapper from './Checkout/PaymentReturn';
import PaymentReturnBase from './Checkout/PaymentReturnBase';

import Cloud from './Cloud';
import CloudCreate from './Cloud/Create';
import CloudExtend from './Cloud/Extend';

import DomainCreate from './DomainCreate';
import DomainRenew from './DomainRenew';
import DomainTransfer from './DomainTransfer';
import FakeOverview from './FakeOverview';
import MailboxCreate from './MailboxCreate';
import { MailboxStandaloneCreate } from './MailboxStandaloneCreate';
import OverviewRoot from './Overview';
import Overview from './Overview/components/Overview';
import SimpleHosting from './SimpleHosting';
import SimpleHostingCreate from './SimpleHosting/Create';
import SimpleHostingRenew from './SimpleHosting/Renew';

import SimpleHostingOneClickRedirect from './SimpleHosting/components/SimpleHostingOneClickRedirect';
import TmchLayout from './Tmch';
import TmchForm from './Tmch/Form';
import TmchOverview from './Tmch/Overview';
import WebHosting from './WebHosting';
import {
  CreateCertificate,
  CreateSHSCertificate,
  RenewCertificate,
} from './certificate';
import { CertificateLayout } from './certificate/CertificateLayout';
import EnsureSSRLoggedInCartOwner from './components/EnsureSSRLoggedInCartOwner';
import NotFound from './components/NotFound';
import Root from './components/Root';
import RootWithPreFetch from './components/RootWithPreFetch';
import { Redirect } from './helpers/router';

export const routes: React.MixedElement = (
  <>
    <Route index element={<Redirect to="/domain/suggest" />} />

    <Route element={<RootWithPreFetch showCartTray />}>
      <Route
        path="simplehosting"
        element={<SimpleHosting />}
        handle={{
          tracking: { chapter2: 'Hosting', chapter3: 'simple_hosting' },
        }}
      >
        <Route
          path="legacy/:cartId?/:productId?"
          element={<SimpleHostingCreate />}
          handle={{ tracking: { name: 'legacy_create' } }}
        />
        {/* old Redirect */}
        <Route
          path="one-click/:cartId?/:productId?"
          element={<SimpleHostingOneClickRedirect />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="renew/:identifier/:cartId?"
          element={<SimpleHostingRenew />}
          handle={{ tracking: { name: 'renew' } }}
        />
      </Route>

      <Route
        path="tmch"
        element={<TmchLayout />}
        handle={{ tracking: { chapter2: 'SMEs', chapter3: 'tmch' } }}
      >
        <Route index element={<TmchOverview />} />
        <Route path="tmch-registration" element={<TmchForm />} />
      </Route>

      <Route
        path="domain"
        handle={{
          tracking: {
            chapter2: 'Domain',
            chapter3: 'suggest',
          },
        }}
      >
        <Route
          index
          element={<Redirect to="suggest" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="suggest/:cartId?"
          element={<DomainCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
        <Route
          path="renew/:cartId?"
          element={<DomainRenew />}
          handle={{ tracking: { name: 'renew' } }}
        />
        <Route
          path="transfer/:cartId?"
          element={<DomainTransfer />}
          handle={{ tracking: { name: 'transfer' } }}
        />
      </Route>

      <Route
        path=":fqdn/mailboxes"
        handle={{ tracking: { chapter2: 'mailboxes' } }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<MailboxCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
      </Route>

      <Route
        path="mailboxes"
        handle={{ tracking: { chapter2: 'mailboxes-standalone' } }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<MailboxStandaloneCreate />}
          handle={{ tracking: { chapter3: 'create', name: '<cart_id>' } }}
        />
      </Route>

      <Route
        path="certificate"
        handle={{ tracking: { chapter2: 'certificate' } }}
        // RR6: migrate with `useCallbackPrompt`
        element={<CertificateLayout />}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<CreateCertificate />}
          handle={{ tracking: { chapter3: 'create', name: '<cart_id>' } }}
        />
        <Route
          path="renew/:certUUID/:cartId?"
          element={<RenewCertificate />}
          handle={{ tracking: { chapter3: 'renew', name: '<cart_id>' } }}
        />
        <Route
          // improvement: `shs` can become a `:context` to be able to buy a cert based on a cn
          path="shs/create/:cn/:cartId?"
          element={<CreateSHSCertificate />}
          handle={{ tracking: { chapter3: 'shs_create', name: '<cart_id>' } }}
        />
      </Route>

      <Route
        path="cloud"
        element={<Cloud />}
        handle={{ tracking: { chapter2: 'Hosting', chapter3: 'cloud' } }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:step?/:cartId?/:productId?"
          element={<CloudCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
        <Route
          path="extend/:cartId?"
          element={<CloudExtend />}
          handle={{ tracking: { name: 'extend' } }}
        />
      </Route>
    </Route>
    <Route element={<RootWithPreFetch />}>
      <Route
        path="cart"
        handle={{ tracking: { chapter2: 'Cart', chapter3: 'overview' } }}
      >
        <Route index element={<FakeOverview />} />

        <Route element={<EnsureSSRLoggedInCartOwner />}>
          <Route path=":cartId">
            <Route
              index
              element={<Redirect to="overview" />}
              handle={{ tracking: { noTrack: true } }}
            />

            <Route element={<OverviewRoot />}>
              <Route
                path="overview/:orderId?"
                element={<Overview />}
                handle={{ tracking: { noTrack: true } }}
              />
            </Route>

            <Route
              path="configure"
              element={<CartConfigure />}
              handle={{ tracking: { chapter3: 'configure' } }}
            >
              <Route
                path="account-choice/confirm/organization/:id"
                handle={{
                  tracking: {
                    chapter3: 'account_choice',
                    name: 'confirm_organization',
                  },
                }}
                element={<AccountChoiceConfirmOrganization />}
              />
              <Route
                path="account-choice/confirm/customer"
                handle={{
                  tracking: {
                    chapter3: 'account_choice',
                    name: 'confirm_customer',
                  },
                }}
                element={<AccountChoiceConfirmCustomer />}
              />
              <Route
                path="account-choice/:action?/:customerId?"
                handle={{
                  tracking: {
                    chapter3: 'account_choice',
                    name: '<customer_id>',
                  },
                }}
                element={<AccountChoice />}
              />
              <Route
                path="account"
                element={<Account />}
                handle={{ tracking: { chapter3: 'account_choice' } }}
              >
                <Route
                  index
                  element={<Redirect to="myself" />}
                  handle={{ tracking: { noTrack: true } }}
                />
                <Route
                  path="new"
                  element={<AccountCreate />}
                  handle={{ tracking: { name: 'orga_create' } }}
                />
                <Route
                  path=":orgId"
                  element={<AccountEdit />}
                  handle={{ tracking: { name: 'orga_edit' } }}
                />
              </Route>
              <Route
                path="customer"
                element={<Customer />}
                handle={{ tracking: { chapter3: 'customer' } }}
              >
                <Route
                  path="create"
                  element={<CustomerCreate />}
                  handle={{ tracking: { name: 'customer_create' } }}
                />
                {/* params.isOwner is deprecated */}
                <Route
                  path=":customerName/:customerId/edit/:isOwner?"
                  handle={{
                    tracking: { name: 'customer_edit' },
                  }}
                  element={<CustomerEdit />}
                />
              </Route>
              <Route
                path="domain-contacts"
                element={<DomainContactsWizard />}
              />
              <Route path=":productId/authinfo" element={<AuthInfo />} />
              <Route path=":productId/claims" element={<Claims />} />
              <Route path=":productId/smd" element={<Smd />} />
              <Route
                path=":productId/contacts/:role"
                handle={{ tracking: { name: 'contact_role' } }}
                element={<DomainContactEdition />}
              />
              <Route
                path="templates"
                element={<Templates />}
                handle={{ tracking: { noTrack: true } }}
              />
              <Route path="setup" handle={{ tracking: { chapter3: 'setup' } }}>
                <Route
                  index
                  element={<Redirect to="overview" />}
                  handle={{ tracking: { noTrack: true } }}
                />
                <Route path="overview/:process?" element={<SetupOverview />} />
                <Route
                  path="role-contact/:role/:process"
                  element={<SetupRoleContact />}
                  handle={{ tracking: { noTrack: true } }}
                />
              </Route>
            </Route>

            <Route
              element={<CheckoutIndex />}
              handle={{ tracking: { chapter3: 'checkout' } }}
            >
              <Route
                path="checkout/:orderId"
                element={<Checkout />}
                handle={{ tracking: { name: '<order_id>' } }}
              />
              <Route
                path="generate-proforma"
                element={<GenerateProforma />}
                handle={{ tracking: { name: 'generate-proforma' } }}
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
    <Route element={<RootWithPreFetch backgroundColor="white" />}>
      <Route
        path="simplehosting"
        element={<SimpleHosting />}
        handle={{
          tracking: { chapter2: 'Hosting', chapter3: 'simple_hosting' },
        }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?/:productId?"
          element={<WebHosting />}
          handle={{ tracking: { name: 'create' } }}
        />
      </Route>
    </Route>

    <Route
      element={<PaymentReturnWrapper />}
      handle={{ tracking: { chapter2: 'Shipping_order' } }}
    >
      <Route
        path="payment-return"
        element={<PaymentReturnBase />}
        handle={{ tracking: { chapter3: 'payment_return' } }}
      />
    </Route>
    <Route
      path="awaiting-payment/:orderId"
      element={<AwaitingPayment />}
      handle={{
        tracking: { chapter3: 'awaiting_payment', name: '<order_id>' },
      }}
    />

    <Route
      path="*"
      element={<NotFound />}
      handle={{ tracking: { chapter3: 'Not_found' } }}
    />
  </>
);

export default (args: any): React.Node => {
  const DecoratedApp = compose(
    provideTranslate(args.translator),
    provideAssetPath(
      args.assetEndpoint,
      args.manifestContent,
      args.assetPrefix,
    ),
  )(Root);

  return (
    <Route
      element={
        <TrackingProvider>
          <Outlet />
        </TrackingProvider>
      }
    >
      <Route
        path="/"
        element={<DecoratedApp />}
        handle={{ tracking: { chapter1: 'Shop' } }}
      >
        {routes}
      </Route>
    </Route>
  );
};
